import React from "react";
import { IoCloseOutline, IoInformationCircleOutline } from "react-icons/io5";
import { ImageComponent } from "../../global/imageComponent";
import { formatOdds, formatBetSlipPoint } from "../../../utility/odds";
import { momentFormatUpperCase } from "../../lib/dateFormat";
import { formatMoney } from "../../lib/wagerFinancials";
import { validateCallBetLeg } from "../../lib/betSlipBuyPoints";
import {
  callBetMarketLabel,
  formatCallBetBookLine,
  resolveCallBetPriceForPayout,
} from "../../../utility/callBetDisplay";

export default function CallBetSlipBuilder({
  items = [],
  totalRisk = 0,
  totalWin = 0,
  maxBetDigits = 7,
  onRemove,
  onDesiredLineChange,
  onDesiredPriceChange,
  onRiskChange,
  onWinChange,
  onSubmit,
}) {
  if (!items.length) {
    return (
      <p className="fs12 mt-2 text-center call-bet-slip__empty">
        Select Spread, M-Line, or Total from any match, then set your desired
        line or price below.
      </p>
    );
  }

  return (
    <>
      <p className="fs11 mg-text-gray1 mb-2 call-bet-slip__intro">
        Set the line or price you want. Your bet activates when the market hits
        your target (limit-order style).
      </p>
      <ul className="call-bet-slip__list">
        {items.map((item, index) => {
          const hasDesiredInput =
            item.type === "h2h"
              ? item.desiredPrice != null && String(item.desiredPrice).trim() !== ""
              : item.desiredPoint != null && String(item.desiredPoint).trim() !== "";
          const legCheck = hasDesiredInput ? validateCallBetLeg(item) : null;
          const payoutPrice =
            resolveCallBetPriceForPayout(item) ?? item.price;

          return (
          <li key={`callbet-slip-${index}`} className="straightBetCard">
            <div className="leftPart">
              <button type="button" onClick={() => onRemove(item, index)}>
                <IoCloseOutline />
              </button>
            </div>
            <div className="rightPart">
              <div>
                <div className="mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <ImageComponent staticImgSrc="cricatIcon" />
                    <p className="fs12">{item.teamName}</p>
                  </div>
                  <div className="d-flex align-items-center gap-4 mt-1">
                    {item.type !== "h2h" ? (
                      <p className="fs12 mb-0">{formatBetSlipPoint(item)}</p>
                    ) : null}
                    <p className="fs12 mb-0">
                      {formatOdds(item.bookPrice ?? item.originalPrice ?? item.price)}
                    </p>
                  </div>
                </div>
              </div>

              <>
                <div className="mb-1">
                  <label className="fs12 mg-test-gray1">
                    {callBetMarketLabel(item.type)}
                  </label>
                </div>

                <div className="mb-2 call-bet-options">
                  <div className="d-flex align-items-center gap-1 mb-1">
                    <label className="fs12 mg-test-gray1 mb-0">Call Bet</label>
                    <IoInformationCircleOutline
                      className="fs14 mg-text-gray1"
                      aria-hidden="true"
                    />
                  </div>
                  <p className="fs11 mg-text-gray1 mb-1">
                    Book: {formatCallBetBookLine(item)}
                  </p>
                {item.type === "h2h" ? (
                  <input
                    className="w-100 inputStyle1 fs12"
                    type="text"
                    inputMode="numeric"
                    placeholder="Desired price (e.g. -600)"
                    value={item.desiredPrice ?? ""}
                    onChange={(e) => onDesiredPriceChange(e.target.value, 0, index)}
                  />
                ) : (
                  <input
                    className="w-100 inputStyle1 fs12"
                    type="text"
                    inputMode="decimal"
                    placeholder="Desired line (e.g. +2.5 or 46.5)"
                    value={item.desiredPoint ?? ""}
                    onChange={(e) => onDesiredLineChange(e.target.value, 0, index)}
                  />
                )}
                {(item.isCallBetType === true || item.isCallBetType === "true") && (
                  <span className="fs11 text-warning d-block mt-1">
                    Pending call bet — activates when market hits your target
                  </span>
                )}
                {legCheck && !legCheck.ok && (
                  <span className="fs11 text-danger d-block mt-1">
                    {legCheck.message}
                  </span>
                )}
              </div>

              <div className="d-flex justify-content-between">
                <p className="fs12 mg-text-gray1">
                  {item?.homeTeam} (vs) {item?.awayTeam}
                </p>
                <p className="fs12 mg-text-gray1">
                  {momentFormatUpperCase(item?.gameTime)}
                </p>
              </div>

              <div className="fs12 d-flex gap-2 my-2">
                <input
                  className="riskAndWin"
                  type="text"
                  inputMode="numeric"
                  placeholder="Risk - $0"
                  maxLength={maxBetDigits}
                  value={item?.risk > 0 ? item?.risk : ""}
                  onChange={(e) => onRiskChange(e.target.value, payoutPrice, 0, index)}
                />
                <input
                  className="riskAndWin"
                  type="text"
                  inputMode="numeric"
                  placeholder="Win - $0"
                  maxLength={maxBetDigits}
                  value={item?.win ? item?.win : ""}
                  onChange={(e) => onWinChange(e.target.value, payoutPrice, 0, index)}
                />
              </div>

              {item?.payout > 0 && (
                <div className="fs14 d-flex gap-2 my-2">
                  <p className="mg-text-green1 fs14">
                    Payout: ${formatMoney(item.payout)}
                  </p>
                </div>
              )}
              </>
            </div>
          </li>
          );
        })}
      </ul>

      <div className="d-flex justify-content-between gap-2 my-0">
        <p className="fs12 mg-text-gray1">Total Risk</p>
        <p className="fs12 riskAndWin">$ {formatMoney(totalRisk)}</p>
      </div>
      <div className="d-flex justify-content-between gap-2 my-0">
        <p className="fs12 mg-text-gray1">Total Win</p>
        <p className="fs12 riskAndWin">$ {formatMoney(totalWin)}</p>
      </div>

      <div className="py-2">
        <button type="button" className="mg-primary-btn fs14" onClick={onSubmit}>
          {items.length > 1 ? "Submit Call Bets" : "Submit Call Bet"}
        </button>
      </div>
    </>
  );
}
